import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useField } from 'formik';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PageStatusIcon from '../PageStatusIcon';
import FormFieldLabel from '../FormFieldLabel';
import Select from '../Select';
import useDebounce from '../../../modules/react/hooks/useDebounce';
import styles from './styles.scss';

const propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  pages: PropTypes.shape({
    id: PropTypes.string,
    internalName: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool,
  className: PropTypes.string,
  onSearch: PropTypes.func,
  searchable: PropTypes.string,
  resetValue: PropTypes.shape({
    id: PropTypes.string,
    internalName: PropTypes.string,
  }),
  showDrafts: PropTypes.bool,
};

const defaultProps = {
  disabled: false,
  loading: false,
  className: undefined,
  onSearch: undefined,
  searchable: false,
  resetValue: undefined,
  showDrafts: undefined,
};

const FormFieldPageSelect = ({
  name,
  label,
  disabled,
  pages,
  loading,
  className,
  onSearch,
  searchable,
  resetValue,
  showDrafts,
  includeDate,
  ...rest
}) => {
  const [field, meta, helpers] = useField(name);
  const error = !isEmpty(meta.error);
  const [value, setValue] = useState('');
  const debouncedValue = useDebounce(value);

  useEffect(() => {
    if (!field.value) {
      helpers.setValue(resetValue);
    }
  }, [resetValue]);

  useEffect(() => {
    if (!searchable) {
      return;
    }

    onSearch(debouncedValue);
  }, [debouncedValue]);

  const fieldClasses = cx(
    styles.field,
    className,
  );

  const optionRenderer = (page) => {
    if (!showDrafts) { return page.internalName; }

    return (
      <>
      <div>
        <PageStatusIcon page={page} />
        {page.internalName}
      </div>
      {includeDate && (
        <div className={styles.date}>
          {page.formattedLastUpdated}
        </div>
      )}
      </>
    );
  };

  const valueRenderer = (page) => {
    if (!showDrafts && page.live) { return page.internalName; }

    return (
      <div>
        <PageStatusIcon page={page} />
        {page.internalName}
      </div>
    );
  };

  return (
    <div className={fieldClasses}>
      <FormFieldLabel
        htmlFor={name}
        title={label}
      />
      <Select
        className={styles.select}
        name={name}
        instanceId="form-field-page-select"
        searchable={searchable}
        options={pages}
        optionRenderer={optionRenderer}
        filterOption={options => options}
        onInputChange={option => setValue(option)}
        value={field.value}
        valueKey="id"
        resetValue={resetValue}
        valueRenderer={valueRenderer}
        onChange={option => helpers.setValue(option)}
        disabled={!!disabled}
        isLoading={loading}
        {...rest}
      />
      {error && (
        <div className={styles.error}>
          {meta.error}
        </div>
      )}
    </div>
  );
};

FormFieldPageSelect.propTypes = propTypes;
FormFieldPageSelect.defaultProps = defaultProps;

FormFieldPageSelect.fragments = {
  pagesConnection: {
    breadcrumbs: gql`
      fragment BreadcrumbParentPageDropdownPagesConnection on PagesConnection {
        edges {
          node {
            id
            title
            internalName
            live
            pageType {
              code
            }
            slug
            breadcrumbTrail {
              id
              name
              ancestorPages {
                id
                live
                breadcrumbTrail {
                  id
                  name
                }
              }
            }
            ...PageStatusIconPage
          }
        }
        totalCount
      }

      ${PageStatusIcon.fragments.page}
    `,
    applyLayout: gql`
      fragment ApplyPageLayoutPagesConnection on PagesConnection {
        edges {
          node {
            id
            title
            internalName
            live
            pageType {
              code
            }
            formattedLastUpdated
            ...PageStatusIconPage
          }
        }
        totalCount
      }

      ${PageStatusIcon.fragments.page}
    `,
  },
};

export default FormFieldPageSelect;
